import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface FeatureFlagContextType {
  featureFlags: Record<string, boolean>;
  isFeatureEnabled: (featureName: string) => boolean;
  loading: boolean;
  refreshFeatureFlags: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const fetchFeatureFlags = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setFeatureFlags({});
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/feature-flags/buddyai/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFeatureFlags({
            buddyAI: data.data.hasAccess || false,
          });
        } else {
          // Feature disabled or error
          setFeatureFlags({
            buddyAI: false,
          });
        }
      } else {
        // If endpoint doesn't exist or returns error, assume feature is disabled
        setFeatureFlags({
          buddyAI: false,
        });
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      // Fail closed - assume features are disabled on error
      setFeatureFlags({
        buddyAI: false,
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  // Refresh when account changes
  useEffect(() => {
    if (isAuthenticated && user?.currentAccountId) {
      fetchFeatureFlags();
    }
  }, [user?.currentAccountId, isAuthenticated, fetchFeatureFlags]);

  const isFeatureEnabled = useCallback((featureName: string): boolean => {
    return featureFlags[featureName] === true;
  }, [featureFlags]);

  const refreshFeatureFlags = useCallback(async () => {
    setLoading(true);
    await fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  return (
    <FeatureFlagContext.Provider
      value={{
        featureFlags,
        isFeatureEnabled,
        loading,
        refreshFeatureFlags,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};
